import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalTechnologyProtectedUnauthorizedItemModel } from 'forms/sections/legal/technology/protectedunauthorized/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import PVQLocation from 'components/Form/PVQLocation';
import Branch from 'components/Form/Branch';
import { isPVQForm } from 'helpers/form';
import { systemITLocationOptions, handlingProtectedInformationDisciplinaryOptions } from 'constants/enums/informationTechnologyOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';

interface Props extends Partial<LegalTechnologyProtectedUnauthorizedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string

}

export default class ProtectedUnauthorizedItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalTechnologyProtectedUnauthorizedItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Incident: this.props.Incident,
      Location: this.props.Location,
      Action: this.props.Action,
      ProtectedInformationAccessLocation: this.props.ProtectedInformationAccessLocation, // PVQOnly
      AccessAttemptOutcome: this.props.AccessAttemptOutcome, // PVQOnly
      AccessAttemptReason: this.props.AccessAttemptReason, // PVQOnly
      AccessAttemptDisciplinaryAction: this.props.AccessAttemptDisciplinaryAction, // PVQOnly
      DisciplinaryActionExplanation: this.props.DisciplinaryActionExplanation, // PVQOnly
      DisciplinaryActionOtherExplanation: this.props.DisciplinaryActionOtherExplanation, // PVQOnly
      ...queue,
    });
  };

  updateDate = (values: LegalTechnologyProtectedUnauthorizedItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateIncident = (values: LegalTechnologyProtectedUnauthorizedItemModel['Incident']) => {
    this.update({
      Incident: values,
    });
  };

  updateLocation = (values: LegalTechnologyProtectedUnauthorizedItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateAccessAttemptOutcome = (values: LegalTechnologyProtectedUnauthorizedItemModel['AccessAttemptOutcome']) => {
    this.update({
      AccessAttemptOutcome: values,
    });
  };

  updateAction = (values: LegalTechnologyProtectedUnauthorizedItemModel['Action']) => {
    this.update({
      Action: values,
    });
  };

  updateAccessAttemptReason = (values: LegalTechnologyProtectedUnauthorizedItemModel['AccessAttemptReason']) => {
    this.update({
      AccessAttemptReason: values,
    });
  };

  updateDisciplinaryActionOtherExplanation = (values: LegalTechnologyProtectedUnauthorizedItemModel['DisciplinaryActionOtherExplanation']) => {
    this.update({
      DisciplinaryActionOtherExplanation: values,
    });
  };

  updateDisciplinaryActionExplanation = (values: LegalTechnologyProtectedUnauthorizedItemModel['DisciplinaryActionExplanation']) => {
    this.update({
      DisciplinaryActionExplanation: values,
    });
  };

  updateAccessAttemptDisciplinaryAction = (values: LegalTechnologyProtectedUnauthorizedItemModel['AccessAttemptDisciplinaryAction']) => {
    const accessAttemptDisciplinaryAction = values?.value;
    const selected = [...((this.props.AccessAttemptDisciplinaryAction || {}).values || [])];
    if (selected.includes(accessAttemptDisciplinaryAction)) {
      selected.splice(selected.indexOf(accessAttemptDisciplinaryAction), 1);
    } else {
      selected.push(accessAttemptDisciplinaryAction);
    }
    this.update({
      AccessAttemptDisciplinaryAction: { values: selected },
      DisciplinaryActionOtherExplanation: selected?.includes('Other') ? this.props.DisciplinaryActionOtherExplanation : {}
    });
  };

  updateProtectedInformationAccessLocation = (values: LegalTechnologyProtectedUnauthorizedItemModel['ProtectedInformationAccessLocation']) => {
    const protectedInformationAccessLocation = values?.value;
    const selected = [...((this.props.ProtectedInformationAccessLocation || {}).values || [])];
    if (selected.includes(protectedInformationAccessLocation)) {
      selected.splice(selected.indexOf(protectedInformationAccessLocation), 1);
    } else {
      selected.push(protectedInformationAccessLocation);
    }
    this.update({
      ProtectedInformationAccessLocation: { values: selected },
      Incident: selected?.includes('Other') ? this.props.Incident : {}
    });
  };

  render() {
    const { formType } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(formType);

    return (
      <div>
        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.date')}
          help="legal.technology.unauthorized.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="legal-technology-unauthorized-date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          attribute={`${attribute}.AccessLocation`}
          title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.accessLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >

          <p>{i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.checkAll')}</p>
          <CheckboxGroup
            className="protected-information-access-location option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.ProtectedInformationAccessLocation || {}).values}
            attribute={`${attribute}.ProtectedInformationAccessLocation`}
          >
            {systemITLocationOptions.map((s, idx) => (
              <Checkbox
                name="protected-information-access-location"
                label={s}
                key={`location-${idx}`}
                value={s}
                className="legal-police-domestic-protected-party-checkbox"
                onUpdate={this.updateProtectedInformationAccessLocation}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>

        {this.props.ProtectedInformationAccessLocation?.values?.includes('Other') && (
          <Field
            title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.otherLocationExplanation')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Incident`}
          >
            <Textarea
              {...this.props.Incident}
              name="Incident"
              onUpdate={this.updateIncident}
              onError={this.props.onError}
              className="legal-technology-unauthorized-incident"
              required={this.props.required}
              attribute={`${attribute}.Incident`}
            />
          </Field>
        )}

        <PVQLocation
          {...this.props.Location}
          attribute={`${attribute}.Location`}
          hideInstallationNameUnknownWhenForeign
          name="Location"
          label={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.location')}
          domesticTitle={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.domesticTitle')}
          foreignTitle={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.foreignTitle')}
          streetLabel={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.streetLabel')}
          militaryDiplomaticInstallationNameLabel={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.installationLabel')}
          includeMilitaryStates
          layout={PVQLocation.ADDRESS}
          className="legal-technology-unauthorized-location"
          geocode
          addressBook="Incident"
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateLocation}
          onError={this.props.onError}
          required={this.props.required}
          isPoBoxAllowed={false}
          showMilitaryInstallation
          showApoFpoDpoPostcodeWhenForeign
          showInternationalStreet={false}
        />

        <Branch
          {...this.props.AccessAttemptOutcome}
          name="AccessAttemptOutcome"
          label={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.accessAttemptOutcome')}
          labelSize="h4"
          className="legal-technology-unauthorized-access-attempt-outcome"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateAccessAttemptOutcome} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
          attribute="AccessAttemptOutcome"
        />
        {this.props.AccessAttemptOutcome?.value === 'Yes' && (
          <Field
            title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.accessedInformationExplanation')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Action`}
          >
            <Textarea
              {...this.props.Action}
              name="Action"
              onUpdate={this.updateAction}
              onError={this.props.onError}
              className="legal-technology-unauthorized-action"
              required={this.props.required}
              attribute={`${attribute}.Action`}
            />
          </Field>
        )}
        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.accessAttemptReason')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AccessAttemptReason`}
        >
          <Textarea
            {...this.props.AccessAttemptReason}
            name="AccessAttemptReason"
            onUpdate={this.updateAccessAttemptReason}
            onError={this.props.onError}
            className="legal-technology-unauthorized-access-attempt-reason"
            required={this.props.required}
            attribute={`${attribute}.AccessAttemptReason`}
          />
        </Field>
        <Field
          attribute={`${attribute}.AccessAttemptDisciplinaryAction`}
          title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.accessAttemptDisciplinaryAction')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <p>{i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.checkAll')}</p>
          <CheckboxGroup
            className="protected-information-access-disciplinary-action option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.AccessAttemptDisciplinaryAction || {}).values}
            attribute={`${attribute}.AccessAttemptDisciplinaryAction`}
          >
            {handlingProtectedInformationDisciplinaryOptions.map((s, idx) => (
              <Checkbox
                name="protected-information-disciplinary-action"
                label={s}
                key={`discipline-${idx}`}
                value={s}
                className="legal-police-disciplinary-action-checkbox"
                onUpdate={this.updateAccessAttemptDisciplinaryAction}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>
        {this.props.AccessAttemptDisciplinaryAction?.values?.includes('Other') && (
          <Field
            title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.disciplinaryActionOtherExplanation')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DisciplinaryActionOtherExplanation`}
          >
            <Textarea
              name="DisciplinaryActionOtherExplanation"
              {...this.props.DisciplinaryActionOtherExplanation}
              onUpdate={this.updateDisciplinaryActionOtherExplanation}
              onError={this.props.onError}
              className="legal-technology-unauthorized-access-discipinary-action"
              required={this.props.required}
              attribute={`${attribute}.DisciplinaryActionOtherExplanation`}
            />
          </Field>
        )}
        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.unauthorized.disciplinaryActionExplanation')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DisciplinaryActionExplanation`}
        >
          <Textarea
            name="DisciplinaryActionExplanation"
            {...this.props.DisciplinaryActionExplanation}
            onUpdate={this.updateDisciplinaryActionExplanation}
            onError={this.props.onError}
            className="legal-technology-unauthorized-access-discipinary-action-explanation"
            required={this.props.required}
            attribute={`${attribute}.DisciplinaryActionExplanation`}
          />
        </Field>
      </div>
    );
  }
}

ProtectedUnauthorizedItem.defaultProps = {
  ProtectedInformationAccessLocation: {}, // PVQOnly
  AccessAttemptOutcome: {}, // PVQOnly
  AccessAttemptReason: {}, // PVQOnly
  AccessAttemptDisciplinaryAction: {}, // PVQOnly
  DisciplinaryActionExplanation: {}, // PVQOnly
  DisciplinaryActionOtherExplanation: {}, // PVQOnly
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,
};
